import React, { useState, useEffect, useRef } from "react";
import { FaCheck, FaGoogle } from "react-icons/fa";
import axios from "axios";
import { API_BASE_URL } from "../utils/urls.js";
import NpsnLogo from "../../assets/npsn_logo.png";
import IntroImage from "../../assets/intelserve.jpg";
import Footer from "./Footer.js";
import Header from "./Header.js";
import { Link, useSearchParams } from "react-router-dom";
import ReactGA from "react-ga4";

const BACKEND_URL = API_BASE_URL;
const GTM_ID = "G-YSTJZ9Q4PH";

const SubscriptionPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const formRef = useRef(null);

  // Helper: Map URL param to Plan Name
  const getPlanNameFromParam = (param) => {
    const p = param?.toLowerCase();
    if (p === "pro") return "Pro Plan";
    if (p === "elite") return "Elite Plan";
    return "Standard Plan";
  };

  // Helper: Map Plan Name to URL param
  const getParamFromPlanName = (name) => {
    if (name === "Pro Plan") return "pro";
    if (name === "Elite Plan") return "elite";
    return "standard";
  };

  const [step, setStep] = useState(1);
  const [isYearly, setIsYearly] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("Standard Plan");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    organizationName: "",
  });

  // --- 1. FIXED GOOGLE TAG MANAGER LOGIC ---
  useEffect(() => {
    if (!document.getElementById("gtm-script")) {
      const script = document.createElement("script");
      script.id = "gtm-script";
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GTM_ID}`;
      script.async = true;

      script.onload = () => {
        window.dataLayer = window.dataLayer || [];
        function gtag() { window.dataLayer.push(arguments); }
        gtag("js", new Date());

        gtag("config", GTM_ID, {
          page_path: "/subscriptions",
          page_title: "Subscription Page Load"
        });

        console.log("Analytics Sent for Subscription Component");
      };

      document.head.appendChild(script);
    }

    return () => {
      const script = document.getElementById("gtm-script");
      if (script) document.head.removeChild(script);
    };
  }, []);

  // --- 2. AUTOMATIC PLAN SELECTION FROM URL ---
  useEffect(() => {
    const planParam = searchParams.get("plan");
    const billingParam = searchParams.get("billing");

    if (planParam || billingParam) {
      const planName = getPlanNameFromParam(planParam);
      const isYearlyBilling = billingParam === "yearly";

      setSelectedPlan(planName);
      setIsYearly(isYearlyBilling);
      setStep(2);

      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 500);
    }
  }, [searchParams]);

  // UPDATED: Forces the URL to show /subscriptions with query params
  const updateParams = (planName, yearly) => {
    const plan = getParamFromPlanName(planName);
    const billing = yearly ? "yearly" : "monthly";
    
    // Update react-router state - this will update the URL query string
    setSearchParams({ plan, billing });
  };

  const toggleBilling = () => {
    setIsYearly(!isYearly);
    // No longer calling updateParams here to avoid triggering the step transition
  };

  const handleCardSelection = (planName) => {
    setSelectedPlan(planName);
    // No longer calling updateParams here to avoid triggering the step transition
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const plans = [
    {
      name: "Standard Plan",
      monthlyPrice: 39,
      yearlyPrice: 339,
      features: ["75 cases/month", "1 default user", "Case management", "Client portal", "GPS check-in", "Affidavit generation", "Stripe payments", "Basic reporting"],
    },
    {
      name: "Pro Plan",
      monthlyPrice: 89,
      yearlyPrice: 889,
      features: ["250 cases/month", "Up to 5 users", "All Standard features", "Advanced scheduling", "Full invoicing", "Case-specific messaging", "Surveillance report generation"],
      isMostPopular: true,
    },
    {
      name: "Elite Plan",
      monthlyPrice: 139,
      yearlyPrice: 1339,
      features: ["Unlimited cases", "Unlimited users", "All Pro features", "Advanced video processing", "Digital signature pad", "Automated workflows", "Client-specific document templates", "Global audit trails", "Advanced reporting & analytics"],
    },
  ];

  const currentPlan = plans.find((p) => p.name === selectedPlan) || plans[0];
  const currentInterval = isYearly ? "yearly" : "monthly";

  const handleContinue = (planName) => {
    setSelectedPlan(planName);
    setStep(2);
    setError(null);
    updateParams(planName, isYearly);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleCancelSubscription = () => {
    setStep(1); // Go back to pricing
    setError(null);
    setSearchParams({}); // Clear URL parameters
  };

  const isFormValid = () => {
    return form.firstName && form.email && form.password && form.organizationName;
  };

  const handleManualSubscribe = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    window.dataLayer.push({
      event: "checkout_started",
      plan: selectedPlan,
      billing: currentInterval
    });

    try {
      const response = await axios.post(`${BACKEND_URL}/subscription/subscribe`, {
        ...form,
        planName: selectedPlan,
        billingInterval: currentInterval,
      });

      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        setError("Subscription initiation failed.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "An error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen mb-10 flex flex-col items-center text-center">
      <Header appName="IntelServe" />

      {/* Hero Section */}
      <div className="bg-sky-700 text-white w-full mb-12">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center gap-10">
          <div className="w-full md:w-1/2 p-4">
            <img src={IntroImage} alt="Product" className="w-full h-auto rounded-xl shadow-2xl object-cover" />
          </div>
          <div className="w-full md:w-1/2 text-center md:text-left">
            <h1 className="text-4xl font-extrabold mb-4">IntelServe: Case Management Reimagined</h1>
            <p className="text-lg font-light mb-10">Automate your process serving and reporting.</p>
            <a href="#prices" className="px-8 py-3 bg-white text-sky-700 font-bold rounded-full shadow-xl inline-block">Signup Now</a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl lg:max-w-6xl w-full mx-auto">
        {step === 0 && (
          <div className="max-w-2xl mx-auto p-12 bg-white rounded-2xl shadow-2xl border border-red-100 mt-10">
            <h2 className="text-4xl font-extrabold text-red-600 mb-4">🚫 Subscription Canceled</h2>
            <button onClick={() => setStep(1)} className="px-8 py-3 bg-sky-600 text-white font-semibold rounded-lg">Re-select Plan</button>
          </div>
        )}

        {step === 2 && (
          <button onClick={() => setStep(1)} className="flex items-center text-gray-600 hover:text-sky-600 mb-6 mx-auto sm:mx-0">&larr; Change Plan</button>
        )}

        {step === 1 && (
          <>
            <div id="prices" className="text-center mb-16">
              <h2 className="text-4xl font-extrabold text-gray-900">Flexible Pricing for Every Team</h2>
              <p className="mt-4 text-xl text-gray-600"><b className="text-sky-600">All plans include a 14-day free trial.</b></p>
            </div>

            <div className="flex justify-center mb-12">
              <div className="relative inline-flex items-center p-1 rounded-full bg-gray-200">
                <span onClick={toggleBilling} className={`px-6 py-2 rounded-full font-medium cursor-pointer transition ${!isYearly ? "bg-white shadow-md" : "text-gray-600"}`}>Monthly</span>
                <span onClick={toggleBilling} className={`px-6 py-2 rounded-full font-medium cursor-pointer transition ${isYearly ? "bg-white shadow-md" : "text-gray-600"}`}>Yearly</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {plans.map((plan) => (
                <div key={plan.name} onClick={() => handleCardSelection(plan.name)} className={`relative flex flex-col p-8 rounded-2xl shadow-xl border-2 transition ${plan.name === selectedPlan ? "border-sky-500 ring-4 ring-sky-500/50 scale-[1.02]" : "border-gray-200 bg-white"}`}>
                  <h3 className="text-2xl font-bold">{plan.name}</h3>
                  <p className="mt-2 text-sm font-bold text-green-600">Includes 14-day free trial</p>
                  <div className="mt-4 flex items-center justify-center">
                    <span className="text-5xl font-extrabold">${isYearly ? plan.yearlyPrice : plan.monthlyPrice}</span>
                    <span className="ml-1 text-xl text-gray-500">/{isYearly ? "year" : "month"}</span>
                  </div>
                  <ul className="mt-8 space-y-4 flex-grow">
                    {plan.features.map((f, i) => <li key={i} className="flex items-start text-left"><FaCheck className="text-sky-500 mt-1 mr-3" />{f}</li>)}
                  </ul>
                  <button onClick={(e) => { e.stopPropagation(); handleContinue(plan.name); }} className="mt-8 w-full py-3 font-semibold rounded-lg bg-sky-600 text-white">Continue to Sign Up</button>
                </div>
              ))}
            </div>
          </>
        )}

        {step === 2 && (
          <div ref={formRef} className="max-w-xl mx-auto p-8 bg-white rounded-2xl shadow-2xl border border-gray-100 mt-10">
            <h2 className="text-3xl font-extrabold mb-2">Start Your {currentPlan.name} Trial</h2>
            <p className="text-lg text-gray-600 mb-6">You get a 14-day free trial. You won't be charged until monthly billing begins.</p>
            <p className="text-lg text-gray-600 mb-6 bg-blue-50 p-3 rounded-lg">Selected: <b>{currentPlan.name}</b> billed <b>{currentInterval}</b></p>

            <form onSubmit={handleManualSubscribe} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="text" name="firstName" placeholder="First Name" onChange={handleFormChange} required className="p-3 border rounded-lg" />
                <input type="text" name="lastName" placeholder="Last Name" onChange={handleFormChange} className="p-3 border rounded-lg" />
              </div>
              <input type="text" name="organizationName" placeholder="Organization" onChange={handleFormChange} required className="w-full p-3 border rounded-lg" />
              <input type="email" name="email" placeholder="Email" onChange={handleFormChange} required className="w-full p-3 border rounded-lg" />
              <input type="password" name="password" placeholder="Password" onChange={handleFormChange} required className="w-full p-3 border rounded-lg" />
              <button type="submit" disabled={isLoading || !isFormValid()} className="w-full py-3 font-semibold rounded-lg bg-sky-600 text-white transition">
                {isLoading ? "Processing..." : "Proceed to Checkout"}
              </button>
              <button type="button" onClick={handleCancelSubscription} className="w-full mt-4 text-sm text-red-500">Cancel & Go Back</button>
            </form>
          </div>
        )}
      </div>
      <Footer NpsnLogo={NpsnLogo} />
    </div>
  );
};

export default SubscriptionPage;